import { createContext, useContext, useState, ReactNode } from 'react'

// Interfaces para os diferentes tipos de respostas
interface IAssociativeAnswer {
  [questionId: number]: { [pairId: number]: string }
}

interface ITrueFalseAnswer {
  [questionId: number]: string
}

interface IMultipleChoiceAnswer {
  [questionId: number]: string
}

// Interface para o contexto de questões
interface IQuestionContext {
  associativeAnswers: IAssociativeAnswer
  trueFalseAnswers: ITrueFalseAnswer
  multipleChoiceAnswers: IMultipleChoiceAnswer
  setAssociativeAnswer: (
    questionId: number,
    pairId: number,
    value: string,
  ) => void
  setTrueFalseAnswer: (questionId: number, value: string) => void
  setMultipleChoiceAnswer: (questionId: number, value: string) => void
  validateAnswers: () => number
  totalPoints: number
  setTotalPoints: (points: number) => void
}

// Criar o contexto
const QuestionContext = createContext<IQuestionContext | undefined>(undefined)

// Props para o provedor de contexto
interface IQuestionProviderProps {
  children: ReactNode
  correctAnswers?: {
    associative?: { [questionId: number]: { [pairId: number]: string } }
    trueFalse?: { [questionId: number]: string }
    multipleChoice?: { [questionId: number]: string }
  }
  totalPoints?: number
}

// Componente provedor
export function QuestionProvider({
  children,
  correctAnswers = {},
  totalPoints = 0,
}: IQuestionProviderProps) {
  const [associativeAnswers, setAssociativeAnswers] =
    useState<IAssociativeAnswer>({})
  const [trueFalseAnswers, setTrueFalseAnswers] = useState<ITrueFalseAnswer>({})
  const [multipleChoiceAnswers, setMultipleChoiceAnswers] =
    useState<IMultipleChoiceAnswer>({})
  const [points, setPoints] = useState(totalPoints)

  // Função para definir resposta associativa
  const setAssociativeAnswer = (
    questionId: number,
    pairId: number,
    value: string,
  ) => {
    setAssociativeAnswers((prev) => ({
      ...prev,
      [questionId]: {
        ...(prev[questionId] || {}),
        [pairId]: value,
      },
    }))
  }

  // Função para definir resposta verdadeiro/falso
  const setTrueFalseAnswer = (questionId: number, value: string) => {
    setTrueFalseAnswers((prev) => ({
      ...prev,
      [questionId]: value,
    }))
  }

  // Função para definir resposta de múltipla escolha
  const setMultipleChoiceAnswer = (questionId: number, value: string) => {
    setMultipleChoiceAnswers((prev) => ({
      ...prev,
      [questionId]: value,
    }))
  }

  // Função para validar respostas e calcular pontuação
  const validateAnswers = () => {
    let score = 0

    // Validar respostas associativas
    if (correctAnswers.associative) {
      Object.keys(correctAnswers.associative).forEach((questionId) => {
        const qId = Number(questionId)
        const userAnswers = associativeAnswers[qId] || {}
        const correct = correctAnswers.associative?.[qId] || {}

        const allCorrect = Object.keys(correct).every(
          (pairId) => userAnswers[Number(pairId)] === correct[Number(pairId)],
        )

        if (allCorrect) {
          score += 1
        }
      })
    }

    // Validar respostas verdadeiro/falso
    if (correctAnswers.trueFalse) {
      Object.keys(correctAnswers.trueFalse).forEach((questionId) => {
        const qId = Number(questionId)
        if (trueFalseAnswers[qId] === correctAnswers.trueFalse?.[qId]) {
          score += 1
        }
      })
    }

    // Validar respostas de múltipla escolha
    if (correctAnswers.multipleChoice) {
      Object.keys(correctAnswers.multipleChoice).forEach((questionId) => {
        const qId = Number(questionId)
        if (
          multipleChoiceAnswers[qId] === correctAnswers.multipleChoice?.[qId]
        ) {
          score += 1
        }
      })
    }

    return score
  }

  return (
    <QuestionContext.Provider
      value={{
        associativeAnswers,
        trueFalseAnswers,
        multipleChoiceAnswers,
        setAssociativeAnswer,
        setTrueFalseAnswer,
        setMultipleChoiceAnswer,
        validateAnswers,
        totalPoints: points,
        setTotalPoints: setPoints,
      }}
    >
      {children}
    </QuestionContext.Provider>
  )
}

// Hook personalizado para usar o contexto
export function useQuestionContext() {
  const context = useContext(QuestionContext)

  if (context === undefined) {
    throw new Error('useQuestionContext must be used within a QuestionProvider')
  }

  return context
}
